import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/** Deploy / local environment keys used across Rind web apps. */
export type RindEnvironmentName = 'local' | 'dev' | 'test' | 'prod' | (string & {});

/**
 * Striped non-production environment banner.
 *
 * Project optional leading content (e.g. status icons) with `[rindEnvLeading]`,
 * and trailing content (e.g. tenant id) as default `ng-content`.
 */
@Component({
  selector: 'rind-env-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './env-banner.scss',
  template: `
    @if (visible()) {
      <div class="rind-env-banner" [class]="bannerClass()">
        <div class="rind-env-banner__leading">
          <h3 class="rind-env-banner__title">Env: <b>{{ displayName() }}</b></h3>
          <ng-content select="[rindEnvLeading]" />
        </div>
        <ng-content />
      </div>
    }
  `,
})
export class EnvBannerComponent {
  /** Environment key from the app `environment.name` file. */
  readonly name = input.required<RindEnvironmentName>();

  protected readonly visible = computed(() => this.name() !== 'prod');
  protected readonly displayName = computed(() => resolveDisplayName(this.name()));
  protected readonly bannerClass = computed(() => resolveBannerClass(this.name()));
}

function resolveDisplayName(name: string): string {
  switch (name) {
    case 'dev':
      return 'Development';
    case 'test':
      return 'Testing';
    case 'prod':
      return 'Production';
    default:
      return 'Local';
  }
}

function resolveBannerClass(name: string): string {
  switch (name) {
    case 'dev':
      return 'banner-development';
    case 'test':
      return 'banner-testing';
    case 'prod':
      return 'banner-production';
    default:
      return 'banner-local';
  }
}
